import React, { useMemo, useState } from 'react';
import type { AssetCategory, AssetLocation, AssetStatus, RestaurantAsset } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import AssetModal from '../components/assets/AssetModal';
import AssetBulkAddModal from '../components/assets/AssetBulkAddModal';
import {
  ASSET_CATEGORIES,
  ASSET_LOCATIONS,
  ASSET_STATUSES,
  assetCategoryLabel,
  assetConditionLabel,
  assetLocationLabel,
  assetStatusLabel,
  categoryQuantityTotals,
  formatAssetCost,
  isAssetLowCount,
} from '../lib/assets';

interface AssetRegisterPageProps {
  assets: RestaurantAsset[];
  loading?: boolean;
  onSave: (asset: RestaurantAsset) => void;
  onBulkSave?: (assets: RestaurantAsset[]) => Promise<void> | void;
  onDelete: (assetId: string) => void;
}

function escapeCsv(value: string | number | undefined | null): string {
  const s = value == null ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const AssetRegisterPage: React.FC<AssetRegisterPageProps> = ({
  assets,
  loading,
  onSave,
  onBulkSave,
  onDelete,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [editing, setEditing] = useState<RestaurantAsset | null>(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<AssetCategory | ''>('');
  const [filterLocation, setFilterLocation] = useState<AssetLocation | ''>('');
  const [filterStatus, setFilterStatus] = useState<AssetStatus | ''>('');
  const [lowOnly, setLowOnly] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (filterCategory && a.category !== filterCategory) return false;
      if (filterLocation && a.location !== filterLocation) return false;
      if (filterStatus && a.status !== filterStatus) return false;
      if (lowOnly && !isAssetLowCount(a)) return false;
      if (!q) return true;
      const hay = [
        a.name,
        a.assetTag,
        a.serialNumber,
        a.supplier,
        a.notes,
        a.condition,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [assets, search, filterCategory, filterLocation, filterStatus, lowOnly]);

  const totals = useMemo(() => categoryQuantityTotals(filtered), [filtered]);
  const lowCount = assets.filter(isAssetLowCount).length;

  const handleOpen = (asset: RestaurantAsset | null = null) => {
    setEditing(asset);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditing(null);
  };

  const handleSave = (asset: RestaurantAsset) => {
    onSave(asset);
    handleClose();
  };

  const handleDelete = (asset: RestaurantAsset) => {
    if (window.confirm(`Delete “${asset.name}” from the asset register?`)) {
      onDelete(asset.id);
    }
  };

  const handleExportCsv = () => {
    const header = [
      'Name',
      'Type',
      'Category',
      'Location',
      'Quantity',
      'Replace below',
      'Status',
      'Asset tag',
      'Serial',
      'Unit cost',
      'Currency',
      'Supplier',
      'Purchase date',
      'Condition',
      'Notes',
    ];
    const rows = filtered.map((a) => [
      escapeCsv(a.name),
      escapeCsv(a.kind === 'tagged' ? 'Tagged item' : 'Quantity line'),
      escapeCsv(assetCategoryLabel(a.category)),
      escapeCsv(assetLocationLabel(a.location)),
      escapeCsv(a.quantity),
      escapeCsv(a.replaceBelow),
      escapeCsv(assetStatusLabel(a.status)),
      escapeCsv(a.assetTag),
      escapeCsv(a.serialNumber),
      escapeCsv(a.unitCost),
      escapeCsv(a.unitCostCurrency),
      escapeCsv(a.supplier),
      escapeCsv(a.purchaseDate ? new Date(a.purchaseDate).toISOString().slice(0, 10) : ''),
      escapeCsv(assetConditionLabel(a.condition)),
      escapeCsv(a.notes),
    ]);
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'asset-register.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-800" data-testid="assets-page-heading">
                Asset Register
              </h1>
              <p className="text-xs text-gray-500 hidden sm:block">
                Reusable restaurant kit — separate from food inventory.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCsv}
                className="bg-white text-gray-700 font-medium py-2 px-3 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Export CSV
              </button>
              {onBulkSave && (
                <button
                  onClick={() => setIsBulkOpen(true)}
                  className="bg-white text-pink-600 font-medium py-2 px-3 rounded-lg border border-pink-300 hover:bg-pink-50"
                >
                  Bulk add
                </button>
              )}
              <button
                onClick={() => handleOpen()}
                className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add asset
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        {totals.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {totals.map((t) => (
              <span
                key={t.category}
                className="inline-flex items-center rounded-full bg-white border border-gray-200 px-3 py-1 text-sm text-gray-700"
              >
                <span className="font-medium">{t.label}</span>
                <span className="ml-2 text-pink-600 font-bold">{t.quantity}</span>
              </span>
            ))}
            {lowCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-3 py-1 text-sm text-amber-800 font-medium">
                {lowCount} below replace count
              </span>
            )}
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-3 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, tag, supplier…"
            className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
          />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as AssetCategory | '')}
            className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
          >
            <option value="">All categories</option>
            {ASSET_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value as AssetLocation | '')}
            className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
          >
            <option value="">All locations</option>
            {ASSET_LOCATIONS.map((l) => (
              <option key={l.id} value={l.id}>{l.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as AssetStatus | '')}
            className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
          >
            <option value="">All statuses</option>
            {ASSET_STATUSES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 px-1">
            <input
              type="checkbox"
              checked={lowOnly}
              onChange={(e) => setLowOnly(e.target.checked)}
            />
            Below replace count
          </label>
        </div>

        <div className="bg-white shadow rounded-lg overflow-x-auto">
          {loading && assets.length === 0 ? (
            <p className="p-6 text-gray-500">Loading assets…</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-gray-500">
              {assets.length === 0
                ? 'No assets yet. Add chafing dishes, plates, uniforms, and other kit you count.'
                : 'No assets match these filters.'}
            </p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tag / serial</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  <th className="relative px-4 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((asset) => {
                  const low = isAssetLowCount(asset);
                  return (
                    <tr key={asset.id} className={low ? 'bg-amber-50' : undefined}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{asset.name}</div>
                        <div className="text-xs text-gray-500">
                          {asset.kind === 'tagged' ? 'Tagged item' : 'Quantity line'}
                          {low ? ' · below replace count' : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {assetCategoryLabel(asset.category)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {assetLocationLabel(asset.location)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {asset.quantity}
                        {asset.kind === 'quantity' && asset.replaceBelow != null && (
                          <span className="text-xs font-normal text-gray-500"> / {asset.replaceBelow}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {assetStatusLabel(asset.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {assetConditionLabel(asset.condition) || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {asset.assetTag || asset.serialNumber || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatAssetCost(asset.unitCost, asset.unitCostCurrency) || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => handleOpen(asset)} className="text-pink-600 hover:text-pink-900 mr-4" title="Edit">
                          <EditIcon className="w-5 h-5" />
                        </button>
                        <button onClick={() => handleDelete(asset)} className="text-red-600 hover:text-red-900" title="Delete">
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
      {isModalOpen && (
        <AssetModal asset={editing} onClose={handleClose} onSave={handleSave} />
      )}
      {isBulkOpen && onBulkSave && (
        <AssetBulkAddModal
          onClose={() => setIsBulkOpen(false)}
          onSave={async (items) => {
            await onBulkSave(items);
            setIsBulkOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default AssetRegisterPage;
